// exercise_picker.jsx — the unified exercise index (user library + 870-entry
// catalog), fast token/fuzzy search, shared row + thumbnail primitives, and the
// reusable ExercisePicker bottom sheet.
//
//   <ExercisePicker open onClose onPick={(exercises[]) => …} multi title />
//
// onPick always receives an ARRAY of state-shaped exercise objects. Catalog
// entries are copied into state.exercises (actions.ensureExercises) before
// onPick fires, so callers can reference the ids immediately.
//
// Shared exports (used by library.jsx / exercise_detail.jsx):
//   useExerciseIndex(), searchItems(), filterItems(), historyStats(),
//   ExerciseThumb, ExerciseRow, FilterChip, SearchField, ProgressiveList,
//   relDay(), MUSCLE_CHIPS, EQUIP_ORDER, CATEGORY_LABEL, DEFAULT_CATEGORIES, PXIcon

import React from 'react';
import { createPortal } from 'react-dom';
import { useSheetHistory } from './sheet_history.js';
import { e1rmRPE, isWorkingSet } from './analytics.js';
import { BUILTIN_MEDIA, catalogToExercise, exerciseImageUrl, useCatalog } from './exercise_catalog.js';
import { MuscleThumb } from './musclemap.jsx';
import { fmtWeight, unitLabel, useApp } from './store.jsx';
import { LL, LLIcon, LLSwitchTrack, LLCheck, LLClose } from './tokens.jsx';
import { historyOf } from './history.js';

// ——— Constants ————————————————————————————————————————————————————————————

export const MUSCLE_CHIPS = ['Chest', 'Back', 'Shoulders', 'Biceps', 'Triceps', 'Quads', 'Hamstrings', 'Glutes', 'Calves', 'Core', 'Forearms', 'Traps', 'Lower Back', 'Adductors', 'Abductors', 'Neck'];
export const EQUIP_ORDER = ['Barbell', 'Dumbbell', 'Machine', 'Cable', 'Smith', 'EZ Bar', 'Kettlebell', 'Bodyweight', 'Bodyweight+', 'Band', 'Medicine Ball', 'Exercise Ball', 'Foam Roll', 'Other'];
export const CATEGORY_LABEL = { strength: 'Strength', olympic: 'Olympic', plyo: 'Plyometric', stretch: 'Stretch', cardio: 'Cardio' };
export const CATEGORY_ORDER = ['strength', 'olympic', 'plyo', 'stretch', 'cardio'];
// Default view hides stretches + cardio (they are reachable via filters or search).
export const DEFAULT_CATEGORIES = ['strength', 'olympic', 'plyo'];
const DAY = 86400000;

// ——— Icons (local) ————————————————————————————————————————————————————————

export const PXIcon = {
  search: (p) => <svg width="18" height="18" viewBox="0 0 20 20" fill="none" {...p}><circle cx="9" cy="9" r="5.5" stroke="currentColor" strokeWidth="1.75"/><path d="M13.2 13.2L17 17" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"/></svg>,
  filter: (p) => <svg width="18" height="18" viewBox="0 0 20 20" fill="none" {...p}><path d="M3 5.5h14M5.5 10h9M8 14.5h4" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"/></svg>,
  heartFill: (p) => <svg width="20" height="20" viewBox="0 0 20 20" {...p}><path d="M10 16.5S3 12 3 7.5a3.5 3.5 0 017-1 3.5 3.5 0 017 1c0 4.5-7 9-7 9z" fill="currentColor" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg>,
  clock: (p) => <svg width="16" height="16" viewBox="0 0 20 20" fill="none" {...p}><circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.6"/><path d="M10 6v4l2.5 1.6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>,
  trophy: (p) => <svg width="18" height="18" viewBox="0 0 20 20" fill="none" {...p}><path d="M6 3.5h8v4a4 4 0 01-8 0v-4z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/><path d="M6 5H3.5v1.2A2.8 2.8 0 006.3 9M14 5h2.5v1.2A2.8 2.8 0 0113.7 9M10 11.5V14M7 16.5h6M8 14h4v2.5H8z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  checkCircle: (p) => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" {...p}><circle cx="12" cy="12" r="11" fill="currentColor"/><path d="M7 12.5l3.2 3.2L17 8.8" stroke="var(--px-ink, #000)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  sparkle: (p) => <svg width="18" height="18" viewBox="0 0 20 20" fill="none" {...p}><path d="M10 3l1.6 4.4L16 9l-4.4 1.6L10 15l-1.6-4.4L4 9l4.4-1.6L10 3z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg>,
};

// Render overlays at <body> level so no ancestor stacking context (screen
// wrappers, transitions) can put the tab bar above a sheet.
// The fixed layer pins absolute-positioned sheets to the viewport even if the
// document itself gets scrolled (iOS focus / scrollIntoView). It ignores
// pointer events; the sheets set pointer-events explicitly when open.
export function Portal({ children }) {
  if (typeof document === 'undefined') return children;
  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 150, pointerEvents: 'none' }}>{children}</div>,
    document.body,
  );
}

// ——— Small utils ————————————————————————————————————————————————————————

const startOfDay = (ts) => { const d = new Date(ts); d.setHours(0, 0, 0, 0); return d.getTime(); };

// "Today" · "Yesterday" · "3d ago" · "2w ago" · "Mar 4" · "Mar 4, 2025"
export function relDay(ts, now = Date.now()) {
  if (!ts) return '';
  const days = Math.round((startOfDay(now) - startOfDay(ts)) / DAY);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return days + 'd ago';
  if (days < 35) return Math.floor(days / 7) + 'w ago';
  const d = new Date(ts);
  const sameYear = d.getFullYear() === new Date(now).getFullYear();
  return d.toLocaleDateString(undefined, sameYear ? { month: 'short', day: 'numeric' } : { month: 'short', day: 'numeric', year: 'numeric' });
}

export function prefersReducedMotion() {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
}

// ——— History stats (one pass over all sessions) ————————————————————————

// Map exerciseId → { count, last, bestE1rm, bestW, bestReps, maxW, maxReps }
export function historyStats(sessions) {
  const m = new Map();
  for (const s of (sessions || [])) {
    if (!s || !s.completedAt) continue;
    const seen = new Set();
    for (const st of (s.sets || [])) {
      if (!isWorkingSet(st)) continue;
      let h = m.get(st.exerciseId);
      if (!h) { h = { count: 0, last: 0, bestE1rm: 0, bestW: 0, bestReps: 0, maxW: 0, maxReps: 0 }; m.set(st.exerciseId, h); }
      if (!seen.has(st.exerciseId)) { seen.add(st.exerciseId); h.count++; if (s.startedAt > h.last) h.last = s.startedAt; }
      const est = e1rmRPE(st.w, st.reps, st.rpe);
      if (est > h.bestE1rm) { h.bestE1rm = est; h.bestW = st.w; h.bestReps = st.reps; }
      if ((st.w || 0) > h.maxW) h.maxW = st.w;
      if (st.reps > h.maxReps) h.maxReps = st.reps;
    }
  }
  return m;
}

// ——— Search ——————————————————————————————————————————————————————————————

function tokenize(s) {
  return String(s || '').toLowerCase().replace(/[’']/g, '').split(/[^a-z0-9]+/).filter(Boolean);
}
const compact = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

const ALIAS = {
  db: ['dumbbell'], dbs: ['dumbbell'], bb: ['barbell'], kb: ['kettlebell'], bw: ['bodyweight'],
  pullup: ['pullups'], pullups: ['pullup'], chinup: ['chin'], chinups: ['chin'],
  abs: ['core', 'ab', 'abdominal', 'crunch'], ab: ['core', 'abs'], lats: ['lat', 'back'], lat: ['lats'],
  pecs: ['chest', 'pec', 'fly'], pec: ['chest'], quad: ['quads'], quads: ['quad', 'squat'],
  hammies: ['hamstrings'], ham: ['hamstrings'], glute: ['glutes', 'hip'], tri: ['triceps'], tris: ['triceps'],
  bi: ['biceps'], bis: ['biceps'], delts: ['shoulders', 'delt', 'deltoid'], delt: ['shoulders'],
  calf: ['calves', 'calf'], calves: ['calf'], traps: ['shrug', 'trap'], fly: ['flyes', 'flye', 'flys', 'butterfly'],
  flies: ['fly', 'flyes', 'flye'], ez: ['ezbar'], smith: ['smith'], press: ['press'],
};

// Is the edit distance between a and b ≤ 1? (fast path for typo tolerance)
function within1(a, b) {
  if (a === b) return true;
  const la = a.length, lb = b.length;
  if (Math.abs(la - lb) > 1) return false;
  if (la === lb) { // adjacent transposition ("benhc" → "bench")
    let k = 0; while (k < la && a[k] === b[k]) k++;
    if (k < la - 1 && a[k] === b[k + 1] && a[k + 1] === b[k] && a.slice(k + 2) === b.slice(k + 2)) return true;
  }
  let i = 0, j = 0, edits = 0;
  while (i < la && j < lb) {
    if (a[i] === b[j]) { i++; j++; continue; }
    if (++edits > 1) return false;
    if (la > lb) i++; else if (lb > la) j++; else { i++; j++; }
  }
  return edits + (la - i) + (lb - j) <= 1;
}

// Typo-tolerant prefix: a is within one edit of w, or of a prefix of w (±1 char).
function fuzzyPrefix(a, w) {
  if (within1(a, w)) return true;
  if (w.length <= a.length) return false;
  return within1(a, w.slice(0, a.length)) || within1(a, w.slice(0, a.length + 1)) || within1(a, w.slice(0, a.length - 1));
}

// Abbreviations that stand for a whole phrase: every word must then match.
const PHRASES = {
  ohp: 'overhead press', rdl: 'romanian deadlift', sldl: 'stiff leg deadlift', ghr: 'glute ham raise',
  skullcrusher: 'skull crusher', skullcrushers: 'skull crusher',
};
function expandQuery(q) {
  return String(q || '').toLowerCase().split(/\s+/).map(w => PHRASES[w] || w).join(' ');
}

function tokenScore(t, it) {
  const variants = ALIAS[t] ? [t, ...ALIAS[t]] : [t];
  let best = 0;
  for (const a of variants) {
    const alias = a !== t;
    for (const w of it._nameWords) {
      // The typed word itself beats a synonym of it ("pullup" ranks Pull-Up
      // over anything matched through an alias).
      if (w === a) { if (!alias) return 4; best = Math.max(best, 3.2); }
      if (w.startsWith(a)) best = Math.max(best, alias ? 2.6 : 3);
    }
    if (best < 2 && a.length >= 3 && it._compact.includes(a)) best = 2;
    if (best < 1.5) for (const w of it._metaWords) if (w.startsWith(a)) { best = 1.5; break; }
    if (best < 1.2 && a.length >= 4) {
      for (const w of it._nameWords) if (fuzzyPrefix(a, w)) { best = 1.2; break; }
      if (best < 0.7) for (const w of it._metaWords) if (fuzzyPrefix(a, w)) { best = 0.7; break; }
    }
  }
  return best;
}

// Ranked search. Every query token must match (prefix / substring / alias / 1-typo).
// Order: a strong text match (every word matched as a word or word prefix) on a
// lift you have logged comes first, by recency and frequency ("bench" → your
// Barbell Bench Press, not Bench Jump); then exact / prefix / in-order matches.
export function searchItems(items, query, now = Date.now()) {
  query = expandQuery(query);
  const qt = tokenize(query);
  if (!qt.length) return items;
  const qc = compact(query);
  const wordIdx = (it, t) => {
    const vs = ALIAS[t] ? [t, ...ALIAS[t]] : [t];
    return it._nameWords.findIndex(w => vs.some(a => w === a || w.startsWith(a)));
  };
  const out = [];
  for (const it of items) {
    let score = 0, ok = true, weakest = 9;
    for (const t of qt) {
      const s = tokenScore(t, it);
      if (!s) { ok = false; break; }
      score += s;
      weakest = Math.min(weakest, s);
    }
    if (!ok) continue;
    if (it._compact === qc) score += 10;          // exact name
    else if (it._compact.startsWith(qc)) score += 4;
    // Words typed in order and adjacent in the name ("db bench" → Dumbbell Bench Press)
    for (let i = 0; i + 1 < qt.length; i++) {
      const a = wordIdx(it, qt[i]), b = wordIdx(it, qt[i + 1]);
      if (a >= 0 && b === a + 1) score += 1.5;
    }
    if (it.h) {
      const recency = Math.max(0, 3 - (now - (it.h.last || 0)) / (10 * DAY)); // 3 → 0 over a month
      score += (weakest >= 2.6 ? 14 : 3) + recency + Math.min(it.h.count, 20) * 0.15;
    }
    if (it.fav) score += 2;
    if (it.category === 'strength') score += 0.5;
    score -= it.name.length * 0.012; // shorter (more canonical) names first
    out.push([score, it]);
  }
  out.sort((a, b) => b[0] - a[0]);
  return out.map(x => x[1]);
}

// Search results as list rows: "Your exercises" (logged before) above "All
// results" whenever the query matches anything you have logged.
export function searchRows(res) {
  if (!res.length) return [];
  const mine = res.filter(it => it.h);
  const label = (n) => (n === 1 ? '1 result' : `${n} results`);
  if (!mine.length) return [{ kind: 'h', key: 'h-res', title: label(res.length), count: null }, ...res.map(it => ({ kind: 'i', key: 'i-' + it.id, item: it }))];
  const rest = res.filter(it => !it.h);
  return [
    { kind: 'h', key: 'h-mine', title: 'Your exercises', count: mine.length },
    ...mine.map(it => ({ kind: 'i', key: 'i-' + it.id, item: it })),
    ...(rest.length ? [{ kind: 'h', key: 'h-res', title: 'All results', count: rest.length }, ...rest.map(it => ({ kind: 'i', key: 'i-' + it.id, item: it }))] : []),
  ];
}

// filters: { muscle, equipment:Set, categories:Set, customOnly, historyOnly }
export function filterItems(items, f, { searching = false } = {}) {
  const cats = f.categories && f.categories.size ? f.categories : null;
  const catsAreDefault = !cats || (cats.size === DEFAULT_CATEGORIES.length && DEFAULT_CATEGORIES.every(c => cats.has(c)));
  return items.filter(it => {
    if (f.muscle && f.muscle !== 'All' && it.group !== f.muscle && !(it.primary || []).includes(f.muscle)) return false;
    if (f.equipment && f.equipment.size && !f.equipment.has(it.equipment)) return false;
    if (f.customOnly && !it.custom) return false;
    if (f.historyOnly && !it.h) return false;
    // A search reaches stretches/cardio unless the user explicitly changed categories.
    if (cats && !(searching && catsAreDefault) && !cats.has(it.category)) {
      // exercises the user already uses are never hidden by the category default
      if (!(catsAreDefault && (it.h || it.custom))) return false;
    }
    return true;
  });
}

// ——— Unified index ————————————————————————————————————————————————————————

function makeItem(base) {
  const it = base;
  it._nameWords = tokenize(it.name);
  it._metaWords = tokenize([it.equipment, it.group, ...(it.primary || []), it.mechanic].filter(Boolean).join(' '));
  it._compact = compact(it.name);
  return it;
}

export function buildIndex(exercises, catalog, stats) {
  const byCat = catalog ? new Map(catalog.map(e => [e.id, e])) : null;
  const shadow = new Set();
  const items = [];
  for (const ex of (exercises || [])) {
    if (!ex || !ex.id) continue;
    const cid = ex.id.startsWith('fx_') ? ex.id : BUILTIN_MEDIA[ex.id];
    if (cid) shadow.add(cid);
    const entry = byCat && cid ? byCat.get(cid) || null : null;
    const group = ex.group || entry?.group || 'Other';
    const primary = ex.primary && ex.primary.length ? ex.primary : (ex.group ? [ex.group] : (entry?.primary || [group]));
    items.push(makeItem({
      id: ex.id, name: ex.name || entry?.name || 'Exercise', group, primary,
      secondary: (ex.secondary ?? entry?.secondary ?? []).filter(m => !primary.includes(m)),
      equipment: ex.equipment || entry?.equipment || 'Other',
      mechanic: ex.mechanic || entry?.mechanic || null,
      force: ex.force || entry?.force || null,
      category: ex.category || entry?.category || 'strength',
      level: entry?.level || null,
      img: (ex.img && ex.img.length ? ex.img : entry?.img) || null,
      unit: ex.unit || entry?.unit || null,
      custom: !!ex.custom, fav: !!ex.fav, yt: ex.yt || '',
      src: 'state', ex, entry, h: stats ? stats.get(ex.id) || null : null,
    }));
  }
  if (catalog) {
    for (const e of catalog) {
      if (shadow.has(e.id)) continue;
      items.push(makeItem({
        id: e.id, name: e.name, group: e.group, primary: e.primary || [e.group],
        secondary: e.secondary || [], equipment: e.equipment, mechanic: e.mechanic, force: e.force || null,
        category: e.category || 'strength', level: e.level, img: e.img, unit: e.unit || null,
        custom: false, fav: false, yt: '', src: 'catalog', ex: null, entry: e,
        h: stats ? stats.get(e.id) || null : null,
      }));
    }
  }
  return items;
}

export function useExerciseIndex() {
  const { state } = useApp();
  const catalog = useCatalog();
  // Keyed on the completed history (history.js): stable while sets are logged.
  const hist = historyOf(state.sessions);
  const stats = React.useMemo(() => historyStats(hist), [hist]);
  const items = React.useMemo(() => buildIndex(state.exercises, catalog, stats), [state.exercises, catalog, stats]);
  return { items, catalog, stats, loading: !catalog };
}

// ——— Smart substitution ————————————————————————————————————————————————

const GENERIC_WORDS = new Set(['the', 'with', 'and', 'exercise', 'machine', 'cable', 'dumbbell', 'barbell', 'smith', 'one', 'arm', 'leg', 'single']);
const normEq = (e) => String(e || '').toLowerCase().replace(/s$/, '').trim();

// Resolve `similarTo` (id string or exercise object) to an index item.
export function resolveTarget(items, similarTo) {
  if (!similarTo) return null;
  const id = typeof similarTo === 'string' ? similarTo : similarTo.id;
  const hit = id ? items.find(it => it.id === id) : null;
  if (hit) return hit;
  if (typeof similarTo !== 'object') return null;
  const o = similarTo;
  const primary = o.primary && o.primary.length ? o.primary : (o.group ? [o.group] : []);
  return makeItem({
    id: o.id || '__target', name: o.name || '', group: o.group || primary[0], primary,
    secondary: (o.secondary || []).filter(m => !primary.includes(m)), equipment: o.equipment || null,
    mechanic: o.mechanic || null, force: o.force || null, category: o.category || 'strength',
    unit: o.unit || null, src: 'state', ex: o, entry: null, h: null,
  });
}

/**
 * Rank substitutes for `target` (an index item). Scoring:
 *   same primary muscle +3 (required) · same mechanic +2 · same force/pattern +2 ·
 *   +1 per shared secondary · equipment used before or in profile +1 · has history +1.
 * Returns [{ item, score, reason }] best first.
 */
export function rankSubstitutes(items, target, { profileEquip = [], limit = 8 } = {}) {
  if (!target) return [];
  const tp = new Set(target.primary || []);
  const ts = new Set(target.secondary || []);
  const used = new Set(items.filter(i => i.h).map(i => normEq(i.equipment)));
  const prof = new Set((Array.isArray(profileEquip) ? profileEquip : []).map(normEq));
  const allowSoft = target.category === 'stretch' || target.category === 'cardio';
  const timed = target.unit === 'sec';
  const targetWords = new Set(target._nameWords || []);
  const out = [];
  for (const it of items) {
    if (it.id === target.id || (target._compact && it._compact === target._compact)) continue;
    if (!allowSoft && (it.category === 'stretch' || it.category === 'cardio')) continue;
    // Not a sensible like-for-like swap for most lifters.
    if (/\bneck\b|behind the neck|guillotine/i.test(it.name)) continue;
    const shared = (it.primary || []).filter(m => tp.has(m));
    if (!shared.length) continue;
    let score = 3;
    const sameMech = !!target.mechanic && it.mechanic === target.mechanic;
    const sameForce = !!target.force && it.force === target.force;
    if (sameMech) score += 2;
    if (sameForce) score += 2;
    score += (it.secondary || []).filter(m => ts.has(m)).length;
    const eq = normEq(it.equipment);
    if (eq && eq === normEq(target.equipment)) score += 1.5;   // same equipment first
    if (used.has(eq) || prof.has(eq)) score += 1;
    if (it.h) score += 1;
    // tie-breakers (together never outweigh a whole criterion)
    if ((it.unit === 'sec') !== timed) score -= 1.5;
    const nameShared = it._nameWords.filter(w => w.length > 2 && !GENERIC_WORDS.has(w) && targetWords.has(w)).length;
    score += Math.min(0.6, nameShared * 0.3);
    if (it.category === 'strength') score += 0.2;
    if (it.level === 'expert') score -= 1;
    if (it.equipment === 'Other') score -= 0.15;
    score -= it.name.length * 0.003;
    const samePrimary = shared.length === tp.size && (it.primary || []).length === tp.size;
    const parts = [
      samePrimary ? (sameMech && sameForce ? 'Same muscles + pattern' : 'Same muscles') : shared.join(' / '),
      sameMech && sameForce ? (samePrimary ? null : 'Same pattern') : sameMech ? (it.mechanic === 'compound' ? 'Compound' : 'Isolation') : null,
      it.equipment,
    ].filter(Boolean);
    out.push({ item: it, score, reason: parts.join(' · ') });
  }
  out.sort((a, b) => b.score - a.score);
  return out.slice(0, limit);
}

// State-shaped exercise object for an index item (catalog → compact copy).
export function itemToExercise(it) {
  if (it.ex) return it.ex;
  return catalogToExercise(it.entry);
}

const byName = (a, b) => a.name.localeCompare(b.name);
const byLast = (a, b) => (b.h?.last || 0) - (a.h?.last || 0);

// ——— Primitives ————————————————————————————————————————————————————————————

// 48px exercise thumbnail. Lists show the mini muscle map (uniform, legible at
// 48px, same language as the workout cards); the stock photo is opt-in
// (`photo`) and used for the detail hero only.
export function ExerciseThumb({ item, size = 48, radius = LL.r.md, style = {}, photo = false }) {
  const src0 = photo && item && item.img && item.img[0] ? exerciseImageUrl(item.img[0]) : '';
  const [failed, setFailed] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const showImg = src0 && !failed;
  return (
    <div style={{
      width: size, height: size, borderRadius: radius, flexShrink: 0, overflow: 'hidden',
      position: 'relative', background: showImg ? '#fff' : LL.surface2,
      boxShadow: `inset 0 0 0 1px ${LL.line}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      ...style,
    }}>
      {showImg ? (
        <img
          src={src0} alt="" loading="lazy" decoding="async" draggable={false}
          onError={() => setFailed(true)} onLoad={() => setLoaded(true)}
          style={{
            width: '100%', height: '100%', objectFit: 'cover', display: 'block',
            opacity: loaded ? 1 : 0, transition: `opacity ${LL.motion.base}ms ${LL.ease}`,
          }}
        />
      ) : (
        <ThumbFallback item={item} size={size} radius={radius} />
      )}
      {showImg && <div style={{ position: 'absolute', inset: 0, borderRadius: radius, boxShadow: `inset 0 0 0 1px color-mix(in oklab, ${LL.line} 70%, transparent)`, pointerEvents: 'none' }} />}
    </div>
  );
}

function ThumbFallback({ item, size, radius = LL.r.md }) {
  return <MuscleThumb primary={item?.primary || []} secondary={item?.secondary || []} size={size} radius={radius} bg={LL.surface2} />;
}

export function FilterChip({ active, onClick, children, icon, badge, ariaLabel }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={!!active} aria-label={ariaLabel} style={{
      height: 34, padding: icon && !children ? '0 10px' : '0 14px', borderRadius: LL.r.pill, flexShrink: 0,
      display: 'inline-flex', alignItems: 'center', gap: 6,
      background: active ? `color-mix(in oklab, ${LL.accent} 18%, ${LL.surface})` : LL.surface,
      border: `1px solid ${active ? `color-mix(in oklab, ${LL.accent} 55%, transparent)` : LL.line}`,
      color: active ? LL.accentText : LL.fgMute,
      fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer',
      transition: `background ${LL.motion.base}ms ${LL.ease}, border-color ${LL.motion.base}ms ${LL.ease}, color ${LL.motion.base}ms ${LL.ease}`,
    }}>
      {icon}
      {children}
      {badge ? (
        <span style={{
          minWidth: 18, height: 18, padding: '0 5px', borderRadius: LL.r.sm, boxSizing: 'border-box',
          background: LL.accent, color: LL.accentInk, fontSize: LL.fs.cap, fontWeight: 700,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontVariantNumeric: 'tabular-nums',
        }}>{badge}</span>
      ) : null}
    </button>
  );
}

export function SearchField({ value, onChange, placeholder = 'Search exercises', inputRef }) {
  return (
    <div style={{ position: 'relative' }}>
      <PXIcon.search style={{ position: 'absolute', left: 13, top: 13, color: LL.fgDim, pointerEvents: 'none' }} />
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        type="search" enterKeyHint="search" autoComplete="off" autoCorrect="off" spellCheck={false}
        aria-label={placeholder}
        className="px-search"
        style={{
          width: '100%', boxSizing: 'border-box', height: 44, padding: '0 40px 0 40px',
          background: LL.surface, color: LL.fg, border: `1px solid ${LL.line}`, borderRadius: LL.r.md,
          fontFamily: LL.ui, fontSize: LL.fs.btn, fontWeight: 500, outline: 'none',
          WebkitAppearance: 'none', appearance: 'none',
        }}
      />
      {value ? (
        <button type="button" aria-label="Clear search" onClick={() => onChange('')} style={{
          position: 'absolute', right: 6, top: 6, width: 32, height: 32, borderRadius: LL.r.pill,
          background: 'transparent', border: 'none', color: LL.fgDim, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
        }}><LLIcon.x style={{ width: 16, height: 16 }} /></button>
      ) : null}
      <style>{`.px-search::-webkit-search-cancel-button{display:none}.px-search::placeholder{color:${LL.fgDim};opacity:1}.px-search:focus{border-color:color-mix(in oklab, ${LL.accent} 60%, ${LL.line})}`}</style>
    </div>
  );
}

// Section header used in long lists.
export function ListSection({ title, count, sticky = true, top = 0, bg = LL.bg }) {
  return (
    <div style={{
      position: sticky ? 'sticky' : 'static', top, zIndex: 2, background: bg,
      padding: '14px 4px 8px', display: 'flex', alignItems: 'baseline', gap: 8,
    }}>
      <span style={{ fontFamily: LL.ui, fontSize: LL.fs.cap, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: LL.fgDim }}>{title}</span>
      {count != null && <span style={{ fontFamily: LL.ui, fontSize: LL.fs.cap, fontWeight: 600, color: LL.fgFaint, fontVariantNumeric: 'tabular-nums' }}>{count}</span>}
    </div>
  );
}

// One exercise row. `trailing` replaces the default right-hand stat block.
export function ExerciseRow({ item, units, onClick, trailing, after, selected, dense = false, showStat = true, reason }) {
  const h = item.h;
  const timed = item.unit === 'sec';
  // The best result rides on the meta line ("Barbell · 2d ago · e1RM 271 lb"),
  // so the name keeps the full row width and wraps to two lines instead of
  // being cut at 360px.
  let stat = null;
  if (showStat && h) {
    if (timed) stat = { pre: 'best', v: String(h.maxReps), u: 's' };
    else if (h.bestE1rm > 0) stat = { pre: 'e1RM', v: fmtWeight(Math.round(h.bestE1rm), units), u: unitLabel(units) };
    else if (h.maxReps > 0) stat = { pre: 'best', v: String(h.maxReps), u: 'reps' };
  }
  const meta = h
    ? [item.equipment, relDay(h.last)].filter(Boolean).join(' · ')
    : [item.group, item.equipment].filter(Boolean).join(' · ');
  return (
    <div
      role="button" tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick && onClick(e); } }}
      className="px-row"
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: dense ? '8px 4px' : '9px 4px', minHeight: dense ? 60 : 66, boxSizing: 'border-box',
        cursor: 'pointer', borderRadius: LL.r.md, position: 'relative',
        background: selected ? `color-mix(in oklab, ${LL.accent} 10%, transparent)` : 'transparent',
        contentVisibility: 'auto', containIntrinsicSize: 'auto 66px',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <ExerciseThumb item={item} size={48} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600, color: LL.fg, letterSpacing: -0.1, lineHeight: 1.25 }}>
          <span style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', overflowWrap: 'anywhere' }}>
            {item.name}
            {item.custom && (
              <span style={{
                marginLeft: 6, verticalAlign: 2, height: 18, padding: '0 6px', borderRadius: LL.r.sm, display: 'inline-flex', alignItems: 'center',
                background: LL.surface3, color: LL.fgMute, fontSize: LL.fs.cap, fontWeight: 600,
              }}>Custom</span>
            )}
          </span>
        </div>
        <div style={{
          marginTop: 3, fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fgMute, fontVariantNumeric: 'tabular-nums',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 5,
        }}>
          {reason ? (
            <span style={{
              maxWidth: '100%', height: 22, padding: '0 8px 0 7px', borderRadius: LL.r.sm, display: 'inline-flex', alignItems: 'center', gap: 6,
              background: LL.surface2, border: `1px solid ${LL.line}`, color: LL.fgMute, fontSize: LL.fs.sm, fontWeight: 500, overflow: 'hidden', boxSizing: 'border-box',
            }}>
              <span style={{ width: 6, height: 6, borderRadius: LL.r.xs, background: LL.accent, flexShrink: 0 }} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{reason}</span>
            </span>
          ) : (
            <>
              {h ? <PXIcon.clock style={{ width: 13, height: 13, color: LL.fgDim, flexShrink: 0 }} /> : null}
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
                {meta}
                {stat ? <> · {stat.pre} <b style={{ color: LL.fg, fontWeight: 700 }}>{stat.v}</b> {stat.u}</> : null}
              </span>
            </>
          )}
        </div>
      </div>
      {trailing !== undefined ? trailing : null}
      {after}
    </div>
  );
}

// Progressive list: renders `rows` in growing chunks as the sentinel nears the
// viewport — keeps 900-row lists instant to open and smooth to scroll.
export function ProgressiveList({ rows, renderRow, step = 50, resetKey, rootRef }) {
  const [n, setN] = React.useState(step);
  const sentinel = React.useRef(null);
  React.useEffect(() => { setN(step); }, [resetKey, step]);
  React.useEffect(() => {
    const el = sentinel.current;
    if (!el || n >= rows.length) return;
    const io = new IntersectionObserver((ents) => {
      if (ents.some(e => e.isIntersecting)) setN(v => Math.min(rows.length, v + step * 2));
    }, { root: rootRef ? rootRef.current : null, rootMargin: '900px 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, [n, rows.length, step, rootRef]);
  return (
    <>
      {rows.slice(0, n).map(renderRow)}
      {n < rows.length && <div ref={sentinel} style={{ height: Math.min(2000, (rows.length - n) * 66) }} />}
    </>
  );
}

// Build the flat row model (section headers + items) for the default
// (no query) view: Recent → Favorites → everything else, history first.
export function buildSections(list, { recentIds = [], showRecent = true, showFavs = true, allTitle = 'All exercises', historyFirst = true }) {
  const rows = [];
  const used = new Set();
  if (showRecent && recentIds.length) {
    const byId = new Map(list.map(it => [it.id, it]));
    const rec = recentIds.map(id => byId.get(id)).filter(Boolean).slice(0, 8);
    if (rec.length) {
      rows.push({ kind: 'h', key: 'h-recent', title: 'Recent', count: null });
      rec.forEach(it => { rows.push({ kind: 'i', key: 'r-' + it.id, item: it }); used.add(it.id); });
    }
  }
  if (showFavs) {
    const favs = list.filter(it => it.fav && !used.has(it.id)).sort(byName);
    if (favs.length) {
      rows.push({ kind: 'h', key: 'h-fav', title: 'Favorites', count: favs.length });
      favs.forEach(it => { rows.push({ kind: 'i', key: 'f-' + it.id, item: it }); used.add(it.id); });
    }
  }
  const rest = list.filter(it => !used.has(it.id));
  if (historyFirst) {
    const hist = rest.filter(it => it.h).sort(byLast);
    const other = rest.filter(it => !it.h).sort(byName);
    if (hist.length) {
      rows.push({ kind: 'h', key: 'h-hist', title: 'Your exercises', count: hist.length });
      hist.forEach(it => rows.push({ kind: 'i', key: 'i-' + it.id, item: it }));
    }
    if (other.length) {
      rows.push({ kind: 'h', key: 'h-all', title: hist.length ? 'More exercises' : allTitle, count: other.length });
      other.forEach(it => rows.push({ kind: 'i', key: 'i-' + it.id, item: it }));
    }
  } else {
    rest.sort(byName);
    rows.push({ kind: 'h', key: 'h-all', title: allTitle, count: rest.length });
    rest.forEach(it => rows.push({ kind: 'i', key: 'i-' + it.id, item: it }));
  }
  return rows;
}

// Most recently performed exercise ids (newest first).
export function recentExerciseIds(sessions, limit = 8) {
  const done = (sessions || []).filter(s => s && s.completedAt).sort((a, b) => b.startedAt - a.startedAt);
  const out = [];
  const seen = new Set();
  for (const s of done) {
    for (const st of (s.sets || [])) {
      if (!st.exerciseId || seen.has(st.exerciseId) || !isWorkingSet(st)) continue;
      seen.add(st.exerciseId); out.push(st.exerciseId);
      if (out.length >= limit) return out;
    }
  }
  return out;
}

// Toggle switch used in filter panels.
export function Toggle({ on, onChange, label, sub }) {
  return (
    <button type="button" role="switch" aria-checked={!!on} onClick={() => onChange(!on)} style={{
      width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0',
      background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left',
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 500, color: LL.fg }}>{label}</div>
        {sub && <div style={{ fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fgMute, marginTop: 2 }}>{sub}</div>}
      </div>
      <LLSwitchTrack on={!!on} />
    </button>
  );
}

// Equipment list present in the index, in a sensible order.
export function equipmentOptions(items) {
  const have = new Set(items.map(it => it.equipment).filter(Boolean));
  const ordered = EQUIP_ORDER.filter(e => have.has(e));
  for (const e of have) if (!ordered.includes(e)) ordered.push(e);
  return ordered;
}

// ——— Picker sheet ————————————————————————————————————————————————————————

function useSheetMount(open, ms = 300) {
  const [mounted, setMounted] = React.useState(open);
  const [shown, setShown] = React.useState(false);
  React.useEffect(() => {
    if (open) {
      setMounted(true);
      const r = requestAnimationFrame(() => requestAnimationFrame(() => setShown(true)));
      return () => cancelAnimationFrame(r);
    }
    setShown(false);
    const t = setTimeout(() => setMounted(false), ms);
    return () => clearTimeout(t);
  }, [open, ms]);
  return { mounted, shown };
}

export function ExercisePicker({ open, onClose, onPick, multi = false, title = 'Add exercise', subtitle, similarTo }) {
  const { mounted, shown } = useSheetMount(open);
  if (!mounted) return null;
  return <Portal><PickerSheet shown={shown} onClose={onClose} onPick={onPick} multi={multi} title={title} subtitle={subtitle} similarTo={similarTo} /></Portal>;
}

function PickerSheet({ shown, onClose, onPick, multi, title, subtitle, similarTo }) {
  const { state, actions } = useApp();
  useSheetHistory(shown, onClose);
  const units = state.prefs?.units || 'lb';
  const { items, loading } = useExerciseIndex();
  const [q, setQ] = React.useState('');
  const dq = React.useDeferredValue(q);
  const [muscle, setMuscle] = React.useState('All');
  const [equip, setEquip] = React.useState(() => new Set());
  const [showAllCats, setShowAllCats] = React.useState(false);
  const [panel, setPanel] = React.useState(false);
  const [sel, setSel] = React.useState([]); // ordered ids (multi)
  const [creating, setCreating] = React.useState(null); // { name }
  const scrollRef = React.useRef(null);
  const sheetRef = React.useRef(null);

  // Keep the sheet above the on-screen keyboard (visualViewport).
  React.useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const fit = () => {
      const el = sheetRef.current; if (!el) return;
      const inset = Math.max(0, window.innerHeight - vv.offsetTop - vv.height);
      el.style.bottom = inset + 'px';
      el.style.maxHeight = Math.max(320, vv.height - 24) + 'px';
    };
    fit();
    vv.addEventListener('resize', fit); vv.addEventListener('scroll', fit);
    return () => { vv.removeEventListener('resize', fit); vv.removeEventListener('scroll', fit); };
  }, []);

  // Escape closes
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const recentIds = React.useMemo(() => recentExerciseIds(state.sessions, 8), [state.sessions]);
  const target = React.useMemo(() => resolveTarget(items, similarTo), [items, similarTo]);
  const equipOpts = React.useMemo(() => equipmentOptions(items), [items]);
  const searching = !!dq.trim();
  const filters = React.useMemo(() => ({
    muscle, equipment: equip,
    categories: showAllCats ? null : new Set(DEFAULT_CATEGORIES),
  }), [muscle, equip, showAllCats]);

  const rows = React.useMemo(() => {
    const filtered = filterItems(items, filters, { searching });
    if (searching) {
      return searchRows(searchItems(filtered, dq));
    }
    const plain = muscle === 'All' && !equip.size;
    if (target) {
      const subs = rankSubstitutes(filtered, target, { profileEquip: state.profile?.equipment, limit: 8 });
      const subIds = new Set(subs.map(x => x.item.id));
      const rest = filtered.filter(it => !subIds.has(it.id) && it.id !== target.id);
      const head = subs.length ? [{ kind: 'h', key: 'h-subs', title: 'Best substitutes', count: null },
        ...subs.map(x => ({ kind: 'i', key: 's-' + x.item.id, item: x.item, reason: x.reason }))] : [];
      return [...head, ...buildSections(rest, { recentIds: [], showFavs: true, historyFirst: true })];
    }
    return buildSections(filtered, { recentIds: plain ? recentIds : [], showFavs: true, historyFirst: true });
  }, [items, filters, searching, dq, muscle, equip, recentIds, target, state.profile]);

  const resultCount = rows.filter(r => r.kind === 'i').length;
  const filterCount = equip.size + (showAllCats ? 1 : 0);

  React.useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = 0; }, [dq, muscle, equip, showAllCats]);

  const commit = (list) => {
    const exs = list.map(itemToExercise);
    const fromCatalog = list.filter(it => it.src === 'catalog').map(it => catalogToExercise(it.entry));
    if (fromCatalog.length) actions.ensureExercises(fromCatalog);
    onPick && onPick(exs);
  };

  const byId = React.useMemo(() => new Map(items.map(it => [it.id, it])), [items]);
  const onRow = (it) => {
    if (!multi) { commit([it]); return; }
    setSel(prev => prev.includes(it.id) ? prev.filter(x => x !== it.id) : [...prev, it.id]);
    try { navigator.vibrate && navigator.vibrate(8); } catch { /* noop */ }
  };
  const addSelected = () => commit(sel.map(id => byId.get(id)).filter(Boolean));

  const toggleEquip = (e) => setEquip(prev => { const n = new Set(prev); if (n.has(e)) n.delete(e); else n.add(e); return n; });

  const reduced = prefersReducedMotion();
  const dur = reduced ? 0 : LL.motion.slow;

  return (
    <>
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.55)', pointerEvents: 'auto',
        backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)',
        opacity: shown ? 1 : 0, transition: `opacity ${dur}ms ${LL.ease}`,
      }} />
      <div
        ref={sheetRef} role="dialog" aria-modal="true" aria-label={title}
        style={{
          position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 201, pointerEvents: 'auto',
          height: 'calc(100dvh - 44px)', maxHeight: 'calc(100dvh - 44px)',
          margin: '0 auto', maxWidth: 560,
          background: LL.bg, borderTopLeftRadius: LL.r.lg, borderTopRightRadius: LL.r.lg,
          border: `1px solid ${LL.line}`, borderBottom: 'none',
          boxShadow: LL.shadow.sheet,
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          transform: shown ? 'translateY(0)' : 'translateY(100%)',
          transition: `transform ${dur}ms ${LL.ease}`,
        }}
      >
        {/* Header */}
        <div style={{ padding: '8px 16px 0', flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'center', paddingBottom: 6 }}>
            <div style={{ width: 40, height: 4, borderRadius: LL.r.xs, background: LL.line2 }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '4px 0 12px' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: LL.display, fontSize: LL.fs.h3, fontWeight: 700, color: LL.fg, letterSpacing: -0.3, lineHeight: 1.1 }}>
                {creating ? 'New exercise' : title}
              </div>
              <div style={{ fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fgMute, marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>
                {creating ? 'Custom exercises live in your library' : (subtitle || (target && target.name ? `Replacing ${target.name}` : loading ? 'Loading exercise library…' : `${items.length} exercises`))}
              </div>
            </div>
            {creating ? (
              <button type="button" onClick={() => setCreating(null)} aria-label="Back to list" style={{
                height: 36, padding: '0 14px', borderRadius: LL.r.pill,
                background: LL.surface2, border: 'none', color: LL.fgMute, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 600,
              }}>Cancel</button>
            ) : <LLClose onClick={onClose} />}
          </div>
          {!creating && (
            <>
              <SearchField value={q} onChange={setQ} />
              <div className="px-noscroll" style={{ display: 'flex', gap: 8, overflowX: 'auto', margin: '10px -16px 0', padding: '0 16px 10px', scrollbarWidth: 'none' }}>
                <FilterChip active={panel || filterCount > 0} onClick={() => setPanel(v => !v)} icon={<PXIcon.filter style={{ width: 16, height: 16 }} />} badge={filterCount || null} ariaLabel="Filters">Filter</FilterChip>
                {['All', ...MUSCLE_CHIPS].map(m => (
                  <FilterChip key={m} active={muscle === m} onClick={() => setMuscle(m)}>{m}</FilterChip>
                ))}
              </div>
              <div style={{
                display: 'grid', gridTemplateRows: panel ? '1fr' : '0fr',
                transition: `grid-template-rows ${reduced ? 0 : LL.motion.base}ms ${LL.ease}`,
              }}>
                <div style={{ overflow: 'hidden' }}>
                  <div style={{ padding: '2px 0 10px' }}>
                    <div style={{ fontFamily: LL.ui, fontSize: LL.fs.cap, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: LL.fgDim, margin: '4px 0 8px' }}>Equipment</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {equipOpts.map(e => <FilterChip key={e} active={equip.has(e)} onClick={() => toggleEquip(e)}>{e}</FilterChip>)}
                    </div>
                    <Toggle on={showAllCats} onChange={setShowAllCats} label="Include stretches & cardio" />
                  </div>
                </div>
              </div>
              <style>{`.px-noscroll::-webkit-scrollbar{display:none}.px-row:active{background:${LL.surface} !important}`}</style>
            </>
          )}
        </div>
        <div style={{ height: 1, background: LL.line, flexShrink: 0 }} />

        {/* Body */}
        {creating ? (
          <QuickCreate
            initialName={creating.name}
            initialMuscle={muscle !== 'All' ? muscle : ''}
            onCreated={(ex) => { onPick && onPick([ex]); }}
          />
        ) : (
          <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', overscrollBehavior: 'contain', padding: '0 12px', paddingBottom: multi && sel.length ? 96 : 28 }}>
            {loading && items.length === 0 ? <ListSkeleton /> : null}
            <ProgressiveList
              rows={rows}
              rootRef={scrollRef}
              resetKey={dq + '|' + muscle + '|' + [...equip].join(',') + '|' + showAllCats}
              renderRow={(r) => r.kind === 'h'
                ? <ListSection key={r.key} title={r.title} count={r.count} bg={LL.bg} />
                : <ExerciseRow
                    key={r.key} item={r.item} units={units}
                    selected={multi && sel.includes(r.item.id)}
                    reason={r.reason}
                    onClick={() => onRow(r.item)}
                    trailing={multi ? <SelectMark on={sel.includes(r.item.id)} n={sel.indexOf(r.item.id) + 1} /> : undefined}
                  />}
            />
            {!loading && resultCount === 0 && (
              <div style={{ padding: '36px 16px 12px', textAlign: 'center' }}>
                <div style={{ width: 52, height: 52, borderRadius: LL.r.pill, margin: '0 auto 12px', background: LL.surface, border: `1px solid ${LL.line}`, color: LL.fgDim, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <PXIcon.search style={{ width: 22, height: 22 }} />
                </div>
                <div style={{ fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600, color: LL.fg }}>
                  {searching ? `No match for “${dq.trim()}”` : 'Nothing matches these filters'}
                </div>
                <div style={{ fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fgMute, marginTop: 4 }}>
                  {searching ? 'Check the spelling, or add it as your own exercise.' : 'Clear a filter to see more.'}
                </div>
              </div>
            )}
            {!loading && !(dq.trim() && items.some(it => it._compact === compact(dq))) && (
              <button type="button" onClick={() => setCreating({ name: dq.trim() })} style={{
                width: '100%', marginTop: 12, display: 'flex', alignItems: 'center', gap: 12,
                padding: '12px 12px', borderRadius: LL.r.md, cursor: 'pointer', textAlign: 'left',
                background: resultCount === 0 ? `color-mix(in oklab, ${LL.accent} 12%, ${LL.surface})` : LL.surface,
                border: `1px ${resultCount === 0 ? 'solid' : 'dashed'} ${resultCount === 0 ? `color-mix(in oklab, ${LL.accent} 45%, transparent)` : LL.line2}`,
              }}>
                <span style={{ width: 40, height: 40, borderRadius: LL.r.md, flexShrink: 0, background: resultCount === 0 ? LL.accent : LL.surface2, color: resultCount === 0 ? LL.accentInk : LL.fgMute, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <LLIcon.plus style={{ width: 20, height: 20 }} />
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600, color: resultCount === 0 ? LL.accentText : LL.fg, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {dq.trim() ? `Create “${dq.trim()}”` : 'Create custom exercise'}
                  </span>
                  <span style={{ display: 'block', fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fgMute, marginTop: 2 }}>Can’t find it? Add your own movement</span>
                </span>
              </button>
            )}
          </div>
        )}

        {/* Multi-select footer */}
        {multi && !creating && (
          <div style={{
            // Above the sticky list headers (zIndex 2), which otherwise paint over the button.
            position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 5, padding: '12px 16px calc(14px + env(safe-area-inset-bottom, 0px))',
            background: `linear-gradient(to top, ${LL.bg} 70%, color-mix(in oklab, ${LL.bg} 0%, transparent))`,
            transform: sel.length ? 'translateY(0)' : 'translateY(110%)',
            transition: `transform ${dur}ms ${LL.ease}`, pointerEvents: sel.length ? 'auto' : 'none',
          }}>
            <button type="button" onClick={addSelected} style={{
              width: '100%', height: 52, borderRadius: LL.r.md, border: 'none', cursor: 'pointer',
              background: LL.accent, color: LL.accentInk, fontFamily: LL.ui, fontSize: LL.fs.btn, fontWeight: 600,
              boxShadow: LL.shadow.md,
            }}>{sel.length === 1 ? 'Add 1 exercise' : `Add ${sel.length} exercises`}</button>
          </div>
        )}
      </div>
    </>
  );
}

// The selection glyph (LLCheck) carrying the pick order: an empty ring, then
// the accent circle with 1, 2, 3… so the order exercises are added in shows.
function SelectMark({ on, n }) {
  return (
    <span style={{ position: 'relative', display: 'inline-flex', marginRight: 4, flexShrink: 0 }}>
      <LLCheck on={false} style={{
        background: on ? LL.accent : 'transparent', border: on ? 'none' : `1.5px solid ${LL.line2}`,
        transform: on ? 'scale(1)' : 'scale(0.92)',
        transition: `transform ${LL.motion.base}ms ${LL.spring}, background ${LL.motion.fast}ms ${LL.ease}`,
      }} />
      {on && <span aria-hidden="true" style={{
        position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: LL.accentInk, fontFamily: LL.ui, fontSize: LL.fs.sm, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
      }}>{n}</span>}
    </span>
  );
}

function ListSkeleton() {
  return (
    <div style={{ paddingTop: 12 }}>
      {Array.from({ length: 7 }).map((_, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 4px' }}>
          <div style={{ width: 48, height: 48, borderRadius: LL.r.md, background: LL.surface }} />
          <div style={{ flex: 1 }}>
            <div style={{ height: 12, width: `${50 + (i * 17) % 35}%`, borderRadius: LL.r.sm, background: LL.surface }} />
            <div style={{ height: 10, width: '30%', borderRadius: LL.r.xs, background: LL.surface, marginTop: 8 }} />
          </div>
        </div>
      ))}
    </div>
  );
}

// Inline "create custom exercise" form inside the picker.
function QuickCreate({ initialName, initialMuscle, onCreated }) {
  const { actions } = useApp();
  const [name, setName] = React.useState(initialName || '');
  const [muscle, setMuscle] = React.useState(initialMuscle || '');
  const [equipment, setEquipment] = React.useState('');
  const [tried, setTried] = React.useState(false);
  const ok = name.trim() && muscle && equipment;
  const submit = () => {
    setTried(true);
    if (!ok) return;
    const nm = name.trim();
    const nice = nm === nm.toLowerCase() ? nm.replace(/[a-z]/g, c => c.toUpperCase()) : nm;
    const ex = { name: nice, group: muscle, primary: [muscle], secondary: [], equipment, mechanic: 'compound', yt: '', notes: '', custom: true, fav: false };
    const id = actions.addCustomExercise(ex);
    onCreated({ ...ex, id });
  };
  const label = (t) => <div style={{ fontFamily: LL.ui, fontSize: LL.fs.cap, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: LL.fgDim, margin: '18px 0 8px' }}>{t}</div>;
  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '4px 16px 24px' }}>
      {label('Name')}
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Landmine Press" autoFocus style={{
        width: '100%', boxSizing: 'border-box', height: 48, padding: '0 14px', borderRadius: LL.r.md,
        background: LL.surface, color: LL.fg, border: `1px solid ${tried && !name.trim() ? LL.danger : LL.line}`,
        fontFamily: LL.ui, fontSize: LL.fs.btn, fontWeight: 500, outline: 'none',
      }} />
      {label('Primary muscle')}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {MUSCLE_CHIPS.map(m => <FilterChip key={m} active={muscle === m} onClick={() => setMuscle(m)}>{m}</FilterChip>)}
      </div>
      {tried && !muscle && <div style={{ fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.danger, marginTop: 6 }}>Pick the main muscle worked</div>}
      {label('Equipment')}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {EQUIP_ORDER.map(e => <FilterChip key={e} active={equipment === e} onClick={() => setEquipment(e)}>{e}</FilterChip>)}
      </div>
      {tried && !equipment && <div style={{ fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.danger, marginTop: 6 }}>Pick the equipment</div>}
      <button type="button" onClick={submit} style={{
        width: '100%', height: 52, marginTop: 26, borderRadius: LL.r.md, border: 'none', cursor: 'pointer',
        background: LL.accent, color: LL.accentInk, fontFamily: LL.ui, fontSize: LL.fs.btn, fontWeight: 600,
        opacity: ok ? 1 : 0.55, transition: `opacity ${LL.motion.base}ms ${LL.ease}`,
      }}>Create and add</button>
      <div style={{ fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fgMute, textAlign: 'center', marginTop: 10 }}>
        You can add secondary muscles, a video and notes later in the Library.
      </div>
    </div>
  );
}
